import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ForumIcon from "@mui/icons-material/Forum";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SchoolIcon from "@mui/icons-material/School";
import {
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  Typography,
  useMediaQuery, // Importar useMediaQuery
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

// No se necesita: const MotionPaper = motion(Paper);

const FeatureShowcase = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [hoveredCard, setHoveredCard] = useState(null);

  // Comprobar preferencia de movimiento reducido
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );

  // Definir variantes de animación condicionales
  const cardVariants = {
    initial: { opacity: 0, y: prefersReducedMotion ? 0 : 20 }, // Sin Y inicial si prefiere reducido
    animate: { opacity: 1, y: 0 },
    hover: { y: prefersReducedMotion ? 0 : -15 }, // Sin Y en hover si prefiere reducido
  };

  // Características para mostrar con enlaces adecuados
  const features = [
    {
      id: "accessibility",
      icon: (
        <AccessibilityNewIcon
          sx={{ fontSize: "2.8rem", color: theme.palette.primary.main }}
        />
      ),
      title: "Accesibilidad",
      description:
        "Herramientas adaptadas para todos los niveles de capacidad cognitiva y sensorial.",
      link: "/resources",
      linkText: "Explorar herramientas",
      color: theme.palette.primary.main, // ¡¡VERIFICAR CONTRASTE AAA!!
    },
    {
      id: "community",
      icon: (
        <ForumIcon
          sx={{ fontSize: "2.8rem", color: theme.palette.secondary.main }}
        />
      ),
      title: "Comunidad",
      description:
        "Conecta con familias que comparten experiencias similares a las tuyas.",
      link: "/forum",
      linkText: "Unirte al foro",
      color: theme.palette.secondary.main, // ¡¡VERIFICAR CONTRASTE AAA!!
    },
    {
      id: "resources",
      icon: (
        <MenuBookIcon
          sx={{ fontSize: "2.8rem", color: theme.palette.success.main }}
        />
      ),
      title: "Recursos",
      description:
        "Materiales educativos, guías y documentación especializada.",
      link: "/resources-art",
      linkText: "Ver materiales",
      color: theme.palette.success.main, // ¡¡VERIFICAR CONTRASTE AAA!!
    },
    {
      id: "education",
      icon: (
        <SchoolIcon
          sx={{ fontSize: "2.8rem", color: theme.palette.info.main }}
        />
      ),
      title: "Formación",
      description: "Cursos y talleres para familias y profesionales.",
      link: "/specialists",
      linkText: "Buscar formación",
      color: theme.palette.info.main, // ¡¡VERIFICAR CONTRASTE AAA!!
    },
  ];

  return (
    // Añadido role="region" y aria-labelledby, usando <section>
    <Box
      component="section"
      aria-labelledby="features-title"
      sx={{
        py: { xs: 5, md: 8 },
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(30,30,50,0.3)"
            : "rgba(240,248,255,0.5)",
        borderRadius: { xs: 0, md: "20px" },
        mx: { xs: 0, md: 2 },
        mb: 6,
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          component="h2"
          align="center"
          id="features-title" // ID para aria-labelledby
          sx={{
            mb: { xs: 5, md: 6 },
            fontFamily: "'Quicksand', sans-serif",
            fontWeight: 700,
            color: theme.palette.primary.dark, // ¡¡VERIFICAR CONTRASTE AAA!!
            position: "relative",
            "&:after": {
              content: '""',
              position: "absolute",
              bottom: -10,
              left: "50%",
              width: "80px",
              height: "4px",
              backgroundColor: theme.palette.secondary.main, // ¡¡VERIFICAR CONTRASTE AAA!!
              borderRadius: "2px",
              transform: "translateX(-50%)",
            },
          }}
        >
          {t("home.featuresTitle", "Características destacadas")}
        </Typography>

        {/* Añadido role="list" y cambiado Grid a ul */}
        <Grid
          container
          spacing={4}
          component="ul"
          sx={{ p: 0, listStyle: "none" }}
        >
          {features.map((feature) => (
            // Añadido role="listitem" y cambiado Grid item a li
            <Grid item xs={12} sm={6} md={3} key={feature.id} component="li">
              {/* Reemplazado MotionPaper por motion.div */}
              <motion.div
                // Usar variantes condicionales para animación
                variants={cardVariants}
                initial="initial"
                animate="animate"
                whileHover="hover"
                transition={{ duration: 0.3 }} // Transición general
                onMouseEnter={() => setHoveredCard(feature.id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{ height: "100%" }} // Asegura que motion.div ocupe la altura del Grid item
              >
                {/* Box interior para link y estilos visuales */}
                <Box
                  component={RouterLink}
                  to={feature.link}
                  sx={{
                    p: 3,
                    pt: 5, // Padding superior para Avatar
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    borderRadius: "16px",
                    textDecoration: "none",
                    color: "inherit", // ¡¡VERIFICAR CONTRASTE AAA DEL TEXTO HEREDADO!!
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: theme.palette.background.paper, // ¡¡VERIFICAR CONTRASTE AAA CON TEXTO!!
                    boxShadow:
                      theme.shadows[hoveredCard === feature.id ? 8 : 4], // Simula elevation
                    transition: "box-shadow 0.3s ease-in-out",
                    // ¡¡ASEGURAR INDICADOR DE FOCO VISIBLE AAA!!
                    // Barra superior coloreada
                    "&:before":
                      hoveredCard === feature.id
                        ? {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "5px",
                            backgroundColor: feature.color, // ¡¡VERIFICAR CONTRASTE AAA!!
                          }
                        : {},
                  }}
                >
                  {/* Contenido de la tarjeta */}
                  <Avatar
                    src="/assets/images/general/VicDown-logo-tagetas.png"
                    alt="VicDown" // Correcto
                    sx={{
                      width: 70,
                      height: 70,
                      position: "absolute",
                      top: -30,
                      left: "50%",
                      transform: "translateX(-50%)",
                      boxShadow: `0 4px 12px ${
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.2)"
                          : "rgba(0,0,0,0.3)"
                      }`,
                      border: `3px solid ${theme.palette.background.paper}`,
                      backgroundColor: theme.palette.background.paper,
                      transition: "all 0.3s",
                      zIndex: 2,
                    }}
                  />

                  <Box
                    sx={{
                      mb: 2,
                      mt: 4, // Margen superior para compensar Avatar
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      backgroundColor:
                        hoveredCard === feature.id
                          ? `${feature.color}22` // ¡¡VERIFICAR CONTRASTE AAA!!
                          : "transparent",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {feature.icon}
                  </Box>

                  <Typography
                    variant="h5"
                    component="h3" // Correcto
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      fontFamily: "'Quicksand', sans-serif",
                      position: "relative",
                      pb: 2, // Padding inferior
                      mb: 2,
                      // Línea eliminada (bloque &:after quitado)
                      // ¡¡VERIFICAR CONTRASTE AAA DEL TEXTO!!
                    }}
                  >
                    {t(`home.features.${feature.id}.title`, feature.title)}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: 3,
                      flexGrow: 1, // Empuja botón abajo
                      // opacity: 0.9, // Eliminado para mejorar contraste AAA
                      // ¡¡VERIFICAR CONTRASTE AAA DEL TEXTO!!
                    }}
                  >
                    {t(
                      `home.features.${feature.id}.description`,
                      feature.description,
                    )}
                  </Typography>

                  <Button
                    variant={
                      hoveredCard === feature.id ? "contained" : "outlined"
                    }
                    size="medium"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      mt: "auto", // Empuja al final
                      borderRadius: "8px",
                      fontWeight: 600,
                      textTransform: "none",
                      transition: "all 0.3s",
                      backgroundColor:
                        hoveredCard === feature.id
                          ? feature.color
                          : "transparent",
                      borderColor: feature.color, // ¡¡VERIFICAR CONTRASTE AAA!!
                      color:
                        hoveredCard === feature.id ? "#fff" : feature.color, // ¡¡VERIFICAR CONTRASTE AAA!!
                      // ¡¡VERIFICAR CONTRASTE AAA EN ESTADO HOVER!!
                      "&:hover": {
                        backgroundColor:
                          hoveredCard === feature.id
                            ? feature.color
                            : `${feature.color}22`,
                        borderColor: feature.color,
                      },
                      // ¡¡ASEGURAR INDICADOR DE FOCO VISIBLE AAA!!
                    }}
                    aria-label={`${feature.title} - ${feature.linkText}`} // Correcto
                  >
                    {t(
                      `home.features.${feature.id}.linkText`,
                      feature.linkText,
                    )}
                  </Button>
                </Box>{" "}
                {/* Fin del Box interior */}
              </motion.div>{" "}
              {/* Fin de motion.div */}
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default FeatureShowcase;
